import { Button, Combobox, ScrollArea, TextInput } from '@any-ui-react/core';
import type { ComboboxItemGroup, ComboboxOptionProps, ComboboxStore } from '@mantine/core';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import {
  type Dispatch,
  type ForwardedRef,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { isGroup } from './group';
import { useSelect } from './hooks';
import type { ComboboxItemWithFormatted, SelectCommonProps, SelectDataProps } from './props';

type BaseProps<TData extends ComboboxItemWithFormatted<TValue>, TValue extends string> = Pick<
  SelectCommonProps,
  | 'className'
  | 'classNames'
  | 'clearable'
  | 'dropdownMinWidth'
  | 'disabled'
  | 'error'
  | 'label'
  | 'maxItemVisible'
  | 'placeholder'
  | 'required'
  | 'searchable'
  | 'selectableDisabledItems'
  | 'withinPortal'
  | 'onSearchChange'
> &
  SelectDataProps<TData>;

interface Props<TData extends ComboboxItemWithFormatted<TValue>, TValue extends string, TInternalValue>
  extends BaseProps<TData, TValue> {
  combobox: ComboboxStore;
  handleValueSelect: (value: string, optionProps: ComboboxOptionProps) => void;
  internalValue: TInternalValue;
  isActive: (value: TValue, internalValue: TInternalValue) => boolean;
  leftSection?: ReactNode;
  refOptions: ForwardedRef<HTMLDivElement> | null;
  renderOption: (item: TData, isActive: boolean) => ReactNode;
  searchRef: MutableRefObject<HTMLInputElement | null>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  showLoadMore?: boolean;
  textValue: string;
  onClickLoadMore?: () => Promise<void>;
  onClickRightSection: () => void;
}

const LOADING = 'Loading...';

export const ComboboxWrapper = <
  TData extends ComboboxItemWithFormatted<TValue>,
  TValue extends string,
  TInternalValue,
>({
  className,
  classNames: _classNames,
  clearable,
  combobox,
  data,
  disabled,
  dropdownMinWidth,
  error,
  handleValueSelect,
  internalValue,
  isActive,
  label,
  leftSection,
  loading,
  maxItemVisible = 5,
  placeholder,
  refOptions,
  renderOption,
  required,
  searchable,
  searchRef,
  searchValue,
  setSearchValue,
  selectableDisabledItems,
  showLoadMore,
  textValue,
  withinPortal,
  onClickLoadMore,
  onClickRightSection,
  onSearchChange,
}: Props<TData, TValue, TInternalValue>) => {
  const { filteredData, optionsMaxHeight } = useSelect({
    combobox,
    data,
    maxItemVisible,
    searchValue,
    setSearchValue,
    onSearchChange,
  });

  const viewport = useRef<HTMLDivElement>(null);
  useEffect(() => {
    viewport.current?.scrollBy({ top: optionsMaxHeight, behavior: 'smooth' });
  }, [optionsMaxHeight]);

  return (
    <Combobox
      disabled={disabled}
      position="bottom-start"
      store={combobox}
      withinPortal={withinPortal}
      onOptionSubmit={handleValueSelect}
    >
      <Combobox.Target>
        <TextInput
          ref={searchRef}
          className={className}
          classNames={{ input: searchable === false ? 'cursor-pointer' : '' }}
          data-1p-ignore={true}
          disabled={disabled || loading}
          error={error}
          label={label}
          leftSection={leftSection}
          placeholder={placeholder}
          readOnly={searchable === false}
          required={required}
          rightSection={
            !disabled &&
            !loading && (
              <>
                {clearable && !isEmpty(internalValue) && !combobox.dropdownOpened ? (
                  <MaterialIcons iconName="cancel" className="cursor-pointer text-gray-6" fill={true} />
                ) : (
                  <MaterialIcons iconName="keyboard_arrow_down" className="cursor-pointer" />
                )}
              </>
            )
          }
          rightSectionPointerEvents={disabled ? 'none' : 'all'}
          rightSectionProps={{ onClick: onClickRightSection, style: { zIndex: 'initial !important' } }}
          spellCheck={false}
          value={combobox.dropdownOpened ? searchValue : loading && !placeholder ? LOADING : textValue}
          onBlur={() => combobox.closeDropdown()}
          onChange={event => setSearchValue(event.currentTarget.value)}
          onClick={() => combobox.openDropdown('mouse')}
          onFocus={() => combobox.openDropdown('keyboard')}
        />
      </Combobox.Target>
      <Combobox.Dropdown
        className={classNames(
          'shadow-dropdown',
          dropdownMinWidth && dropdownMinWidth > 0 ? `min-w-[${dropdownMinWidth}px]` : undefined,
          _classNames?.dropdown,
        )}
      >
        <Combobox.Options ref={refOptions} className={_classNames?.options}>
          {loading ? (
            <Combobox.Empty className={classNames('text-left', _classNames?.empty)}>
              <span className="text-sm">{LOADING}</span>
            </Combobox.Empty>
          ) : isEmpty(filteredData) ? (
            <Combobox.Empty className={classNames('text-left', _classNames?.empty)}>
              <span className="text-sm">No Option</span>
            </Combobox.Empty>
          ) : (
            <ScrollArea.Autosize
              mah={optionsMaxHeight}
              offsetScrollbars="present"
              scrollbars="y"
              type="always"
              viewportRef={viewport}
            >
              {filteredData.map((it, idx) => {
                if (isGroup(it as unknown)) {
                  const group = it as unknown as ComboboxItemGroup;
                  return (
                    <Combobox.Group key={group.group ?? idx} className={_classNames?.group} label={group.group}>
                      {group.items.map(sub => {
                        const subItem = typeof sub === 'string' ? { value: sub, label: sub } : sub;
                        const active = isActive((subItem.value ?? '') as TValue, internalValue);
                        return (
                          <Combobox.Option
                            key={subItem.value}
                            active={active}
                            className={_classNames?.option}
                            disabled={!selectableDisabledItems && subItem.disabled}
                            value={subItem.value ?? ''}
                          >
                            {renderOption(subItem as unknown as TData, active)}
                          </Combobox.Option>
                        );
                      })}
                    </Combobox.Group>
                  );
                }
                const active = isActive(it.value, internalValue);
                return (
                  <Combobox.Option
                    key={it.value}
                    active={active}
                    className={_classNames?.option}
                    disabled={!selectableDisabledItems && it.disabled}
                    value={it.value}
                  >
                    {renderOption(it, active)}
                  </Combobox.Option>
                );
              })}
              {showLoadMore && isEmpty(searchValue.trim()) && (
                <>
                  <hr />
                  <Button
                    className="text-gray-9 hover:bg-gray-0"
                    fullWidth={true}
                    size="sm"
                    variant="transparent"
                    onClick={onClickLoadMore}
                  >
                    Load more
                  </Button>
                </>
              )}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
